import { useState, useRef, useEffect } from "react"
import { Upload, Download, CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"
import { Toast } from "primereact/toast"
import API_BASE_URL from "../../Config"
import { ClimbingBoxLoader } from "react-spinners"

const Holidays = () => {
  const [uploadedFile, setUploadedFile] = useState(null)
  const [entityList, setEntityList] = useState([])
  const [selectedEntity, setSelectedEntity] = useState("")
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()) // Store year as number
  const [loading, setLoading] = useState(false)
  const toast = useRef(null)
  const fileInputRef = useRef(null)

  // State for custom year picker overlay
  const [isYearPickerOpen, setIsYearPickerOpen] = useState(false)
  const [yearRangeStart, setYearRangeStart] = useState(new Date().getFullYear() - 10) // Start range 10 years back
  const yearPickerRef = useRef(null)

  // Extract user data
  const roleId = Number.parseInt(sessionStorage.getItem("user_role_id"), 10)
  const clientId = sessionStorage.getItem("client_id")
  //const username = sessionStorage.getItem("current_username")
  //const companyName = sessionStorage.getItem("company_name")
  const token = sessionStorage.getItem("token")

  // Generate years for the current view in the custom picker
  const yearsInView = Array.from({ length: 20 }, (_, i) => yearRangeStart + i) // Show 20 years at a time

  // Close year picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (yearPickerRef.current && !yearPickerRef.current.contains(event.target)) {
        setIsYearPickerOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Fetch entities if superadmin
  useEffect(() => {
    if (roleId === 1) {
      fetch(`${API_BASE_URL}api/overall_clients/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Client API response:", data)
          if (data.data && Array.isArray(data.data.client)) {
            setEntityList(data.data.client)
          } else {
            setEntityList([])
          }
        })
        .catch(() => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to load clients.",
            life: 0, // Never auto-hide
            closable: true,
            sticky: true,
          })
        })
    }
  }, [roleId, token])

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file && /\.(xlsx)$/i.test(file.name)) {
      setUploadedFile(file)
    } else {
      toast.current?.show({
        severity: "warn",
        summary: "Invalid File",
        detail: "Only .xlsx files are allowed.",
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      })
    }
  }

  const handleRemoveFile = () => {
    setUploadedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const showWarn = (summary, detail) => {
    toast.current?.show({
      severity: "warn",
      summary: summary,
      detail: detail,
      life: 0, // Never auto-hide
      closable: true,
      sticky: true,
    })
  }

  const handleDownload = async () => {
    const token = sessionStorage.getItem("token")
    const roleId = sessionStorage.getItem("user_role_id")
    const username = sessionStorage.getItem("current_username")
    const companyName = roleId === "1" ? selectedEntity : sessionStorage.getItem("company_name")

    if (!selectedYear) {
      showWarn("Year Required", "Please select a valid year before downloading.")
      return
    }

    if (!companyName) {
      showWarn("Company Missing", "Please select or log in with a valid company.")
      return
    }

    if (roleId === "3" && !username) {
      showWarn("Missing Username", "Username is required for role 3 to download.")
      return
    }

    setLoading(true)
    try {
      let query = `client_id=${clientId}`
      if (roleId === "3") {
        query += `&username=${encodeURIComponent(username)}`
      }
      query += `&period=${selectedYear}` // Use selectedYear directly

      const res = await fetch(`${API_BASE_URL}api/holidayfile_download/?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!res.ok) {
        toast.current?.show({
          severity: "warn",
          summary: "Download Failed",
          detail: res.status === 404 ? "Holiday Master not available for this company." : "Failed to download file.",
          life: 0, // Never auto-hide
          closable: true,
          sticky: true,
        })
        return
      }

      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url

      // Extract filename from Content-Disposition header
      let filename = "HolidayMaster.xlsx" // Default fallback
      const contentDisposition = res.headers.get("Content-Disposition")
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "")
        }
      }
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.current?.show({
        severity: "success",
        summary: "Download Complete",
        detail: "Holiday Master downloaded successfully!",
        life: 6000,
        closable: true,
      })
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Download Error",
        detail: error.message,
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedYear) {
      showWarn("Year Required", "Please select a valid year before uploading.")
      return
    }

    if (!uploadedFile) {
      showWarn("Upload Required", "Please upload a file before submitting.")
      return
    }

    setLoading(true)
    const formData = new FormData()
    formData.append("holiday_master", uploadedFile)
    formData.append("client_id", clientId)
    //formData.append("username", username || "")
    //formData.append("company_name", companyName || "")
    //formData.append("role_id", roleId)
    formData.append("period", selectedYear) // Use selectedYear directly
    if (roleId === 1) formData.append("entity_id", selectedEntity)

    try {
      const response = await fetch(`${API_BASE_URL}api/upload_holidays/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      const contentDisposition = response.headers.get("Content-Disposition")
      const contentType = response.headers.get("Content-Type")

      // Check if response is a file (error file download)
      const isFile =
        (contentDisposition && contentDisposition.includes("attachment")) ||
        (contentType &&
          (contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
            contentType.includes("application/vnd.ms-excel") ||
            contentType.includes("application/octet-stream")))

      if (isFile) {
        // Backend returned an error file — download it
        const blob = await response.blob()

        // Extract exact filename from Content-Disposition header
        let filename = "error_file.xlsx" // default fallback
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "")
          }
        }
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = filename // This line is already correct
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)

        // Clear the uploaded file
        handleRemoveFile()
        toast.current?.show({
          severity: "error",
          summary: "Upload Failed - Error File Downloaded",
          // detail: "An error occurred during upload. Please check the downloaded file for details.",
          life: 0, // Never auto-hide
          closable: true,
          sticky: true,
        })
      } else if (response.ok) {
        // Successful upload without error file
        const data = await response.json()
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: data?.message || "File uploaded successfully.",
          life: 0, // Never auto-hide
          closable: true,
          sticky: true,
        })
        handleRemoveFile()
        setSelectedEntity("")
      } else {
        // Regular error response
        const data = await response.json()

        // Clear the uploaded file on any error
        handleRemoveFile()

        toast.current?.show({
          severity: "error",
          summary: `Upload Failed (${response.status})`,
          detail: data?.message || "An error occurred.",
          life: 0, // Never auto-hide
          closable: true,
          sticky: true,
        })
      }
    } catch (err) {
      // Clear the uploaded file on any error
      handleRemoveFile()

      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.message,
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClear = () => {
    handleRemoveFile()
    if (roleId === 1) setSelectedEntity("")
    setSelectedYear(new Date().getFullYear()) // Reset to current year
  }

  const handleYearSelect = (year) => {
    setSelectedYear(year)
    setIsYearPickerOpen(false)
  }

  const navigateYears = (direction) => {
    setYearRangeStart((prev) => prev + direction * 20) // Navigate 20 years at a time
  }

  return (
    <div className="relative">
      {/* Loader Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <ClimbingBoxLoader color="#d97925" size={20} />
        </div>
      )}
      <div
        className="flex flex-col items-center justify-center bg-cover bg-center p-4 text-white"
        style={{ backgroundImage: "url('/background.jpg')" }}
      >
        <Toast ref={toast} />
        <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl shadow-lg p-8 w-full max-w-md">
          <h2 className="text-2xl font-bold mb-6 text-center">Holiday Master Configuration</h2>

          {/* Custom Year Picker */}
          <div className="mb-6 relative" ref={yearPickerRef}>
            <label htmlFor="year-picker-display" className="block mb-3 text-sm font-medium flex items-center gap-2">
              <CalendarDays size={18} className="text-[#d97925]" />
              Select Year
            </label>
            <div
              id="year-picker-display"
              className="w-full h-10 rounded-lg bg-white/10 border border-white/20 flex items-center justify-between px-2 cursor-pointer transition-all duration-200 hover:bg-white/15"
              onClick={() => setIsYearPickerOpen(!isYearPickerOpen)}
            >
              <span className="text-white text-sm font-medium">{selectedYear || "Select Year"}</span>
              <ChevronDown
                size={16}
                className={`text-[#d97925] transition-transform ${isYearPickerOpen ? "rotate-180" : ""}`}
              />
            </div>

            {isYearPickerOpen && (
              <div className="absolute z-10 mt-2 w-full max-w-[180px] mx-auto bg-gray-800 rounded-lg shadow-lg border border-white/20 p-3">
                <div className="flex justify-between items-center mb-3">
                  <button
                    onClick={() => navigateYears(-1)}
                    className="p-1.5 rounded-full hover:bg-white/20 text-white transition"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm font-semibold text-white">
                    {yearRangeStart} - {yearRangeStart + 19}
                  </span>
                  <button
                    onClick={() => navigateYears(1)}
                    className="p-1.5 rounded-full hover:bg-white/20 text-white transition"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {yearsInView.map((year) => (
                    <button
                      key={year}
                      className={`w-full aspect-square flex items-center justify-center rounded-md text-white text-sm font-medium transition ${
                        selectedYear === year ? "bg-orange-500 hover:bg-orange-600" : "bg-white/10 hover:bg-white/20"
                      }`}
                      onClick={() => handleYearSelect(year)}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Entity Dropdown for Superadmin */}
          {roleId === 1 && (
            <div className="mb-6">
              <label htmlFor="entity" className="block mb-2 text-sm">
                Select Entity
              </label>
              <select
                id="entity"
                value={selectedEntity}
                onChange={(e) => setSelectedEntity(e.target.value)}
                className="w-full px-4 py-2 rounded-md bg-white/10 border border-white/20 text-black focus:outline-none focus:ring-2 focus:ring-[#d97925]"
              >
                <option value="">-- Select Entity --</option>
                {entityList.map((entity, index) => (
                  <option key={index} value={entity}>
                    {entity}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Download Button */}
          <div className="flex flex-col items-center mb-6">
            <button
              onClick={handleDownload}
              className="text-[#d97925] hover:text-white bg-white hover:bg-[#d97925] border border-[#d97925] rounded-full p-3 transition-all"
            >
              <Download size={20} />
            </button>
            <span className="mt-2 text-sm">Download Holiday Document</span>
          </div>
          {/* Upload Section - ALWAYS VISIBLE */}
          <>
            <div className="flex flex-col items-center mb-4">
              <label className="cursor-pointer">
                <div className="text-[#d97925] hover:text-white bg-white hover:bg-[#d97925] border border-[#d97925] rounded-full p-3 transition-all flex items-center justify-center">
                  <Upload size={20} />
                  <input type="file" accept=".xlsx" onChange={handleFileChange} className="hidden" ref={fileInputRef} />
                </div>
              </label>
              <span className="mt-2 text-sm">Upload Holiday Document (.xlsx)</span>
            </div>
            {/* Uploaded File Preview */}
            {uploadedFile && (
              <div className="flex justify-between items-center bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-4 text-sm text-white">
                <span className="truncate">{uploadedFile.name}</span>
                <button onClick={handleRemoveFile} className="text-red-400 hover:text-red-600 ml-4 text-sm">
                  Remove
                </button>
              </div>
            )}
            {/* Submit and Clear Buttons */}
            <div className="flex gap-4 justify-center mt-4">
              <button
                onClick={handleSubmit}
                className="bg-[#d97925] hover:bg-white hover:text-[#d97925] text-white px-4 py-2 rounded-full border border-[#d97925] transition-all"
              >
                Submit
              </button>
              <button
                onClick={handleClear}
                className="bg-white hover:bg-[#d97925] hover:text-white text-[#d97925] px-4 py-2 rounded-full border border-[#d97925] transition-all"
              >
                Clear
              </button>
            </div>
          </>
        </div>
      </div>
    </div>
  )
}

export default Holidays
